import { configureStore } from '@reduxjs/toolkit';
import { persistReducer, persistStore } from 'redux-persist';
import { combineReducers } from 'redux';
import { authSlice, rtkQueryErrorLogger } from 'store/slices';
import {
  blockerModalsState,
  dashboardState,
  drawerState,
  heliosConditionsState,
  heliosNotificationConditionsState,
  modulesAndAgendasState,
  navigationState,
  tablesQueryState,
  templateFormsState,
  uiState,
} from './entities';
import { coreApi } from 'store/services/core';
import { documentStorageApi } from 'store/services/documentStorage';
import { flowApi } from 'store/services/flow';
import { heliosApi } from 'store/services/helios';
import { chatbotState } from './entities/chatbot';
import { migrateChatbotPersistence, migrateStandaloneChatboxPersistence, persistMigratedChatbotState } from 'utils/chatbotPersistence';
import { initRecordReturnTo } from 'utils/agendaRecord/recordReturnTo';
import { sanitizeDocumentAction, sanitizeDocumentState } from 'utils/documentEditing/documentDevTools';

const MAX_PERSIST_SIZE = 600 * 1024; // 600KB hard cap

const PERSIST_KEY = 'chatbot-history';
const LEGACY_PERSIST_KEYS = ['flowio-persist-v2', 'root'];
const PERSIST_STORAGE_KEY = `persist:${PERSIST_KEY}`;
const STANDALONE_CHATBOX_KEYS = ['chatbox:size', 'chatbox:history', 'chatbox:open'];

const storage = {
  getItem: (key: string) => {
    const persistedValue = localStorage.getItem(key);
    if (key !== PERSIST_STORAGE_KEY) return Promise.resolve(persistedValue);
    if (persistedValue) return Promise.resolve(migrateChatbotPersistence(persistedValue) ?? persistedValue);

    for (const legacyKey of LEGACY_PERSIST_KEYS) {
      const legacyStorageKey = `persist:${legacyKey}`;
      const legacyValue = localStorage.getItem(legacyStorageKey);
      if (!legacyValue) continue;

      const migratedValue = migrateChatbotPersistence(legacyValue);
      if (!migratedValue) continue;

      persistMigratedChatbotState(localStorage, key, legacyStorageKey, migratedValue);

      return Promise.resolve(migratedValue);
    }

    const standaloneMigratedValue = migrateStandaloneChatboxPersistence(
      localStorage.getItem('chatbox:size'),
      localStorage.getItem('chatbox:history'),
      localStorage.getItem('chatbox:open')
    );
    if (standaloneMigratedValue) {
      for (const standaloneKey of STANDALONE_CHATBOX_KEYS) {
        persistMigratedChatbotState(localStorage, key, standaloneKey, standaloneMigratedValue);
      }
      return Promise.resolve(standaloneMigratedValue);
    }

    return Promise.resolve(null);
  },
  setItem: (key: string, item: string) => {
    if (item.length > MAX_PERSIST_SIZE) {
      return Promise.resolve();
    }
    return Promise.resolve(localStorage.setItem(key, item));
  },
  removeItem: (key: string) => Promise.resolve(localStorage.removeItem(key)),
};

const persistConfig = {
  key: PERSIST_KEY,
  storage,
  whitelist: ['chatbot'],
  blacklist: [coreApi.reducerPath, documentStorageApi.reducerPath, flowApi.reducerPath, heliosApi.reducerPath],
  throttle: 2000,
};

const rootReducer = combineReducers({
  [coreApi.reducerPath]: coreApi.reducer,
  [documentStorageApi.reducerPath]: documentStorageApi.reducer,
  [flowApi.reducerPath]: flowApi.reducer,
  [heliosApi.reducerPath]: heliosApi.reducer,
  auth: authSlice.reducer,
  tablesQuery: tablesQueryState.reducer,
  modulesAndAgendas: modulesAndAgendasState.reducer,
  heliosTemplateForms: templateFormsState.reducer,
  navigationState: navigationState.reducer,
  dashboard: dashboardState.reducer,
  drawerState: drawerState.reducer,
  heliosConditions: heliosConditionsState.reducer,
  heliosNotificationConditions: heliosNotificationConditionsState.reducer,
  blockerModals: blockerModalsState.reducer,
  uiState: uiState.reducer,
  chatbot: chatbotState.reducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  devTools: { actionSanitizer: sanitizeDocumentAction, stateSanitizer: sanitizeDocumentState },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }).concat(coreApi.middleware, documentStorageApi.middleware, flowApi.middleware, heliosApi.middleware, rtkQueryErrorLogger),
  //   devTools: process.env.TYPE === 'local',
});

initRecordReturnTo(store);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
